import { TABLE_MAX_COLS, TABLE_MAX_ROWS } from './tableModel';

export interface ClipboardTableParse {
  grid: string[][];
  truncated: boolean;
}

function decodeEntities(value: string): string {
  return value
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

function stripTags(html: string): string {
  return decodeEntities(String(html || '').replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, ' '))
    .replace(/\s+/g, ' ')
    .trim();
}

function truncateGrid(grid: string[][]): { grid: string[][]; truncated: boolean } {
  const truncated = grid.length > TABLE_MAX_ROWS || grid.some((row) => row.length > TABLE_MAX_COLS);
  return {
    truncated,
    grid: grid.slice(0, TABLE_MAX_ROWS).map((row) => row.slice(0, TABLE_MAX_COLS)),
  };
}

function parseHtmlTable(html: string): string[][] | null {
  const table = html.match(/<table\b[\s\S]*?<\/table>/i);
  if (!table) return null;
  const rows: string[][] = [];
  const trRe = /<tr\b[\s\S]*?<\/tr>/gi;
  let tr = trRe.exec(table[0]);
  while (tr) {
    const cells: string[] = [];
    const cellRe = /<t[dh]\b[\s\S]*?<\/t[dh]>/gi;
    let cell = cellRe.exec(tr[0]);
    while (cell) {
      cells.push(stripTags(cell[0]));
      cell = cellRe.exec(tr[0]);
    }
    if (cells.length) rows.push(cells);
    tr = trRe.exec(table[0]);
  }
  return rows.length ? rows : null;
}

function splitCsvLine(line: string, delimiter: string): string[] {
  const cells: string[] = [];
  let current = '';
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === '"') {
      if (quoted && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
      continue;
    }
    if (char === delimiter && !quoted) {
      cells.push(current.trim());
      current = '';
      continue;
    }
    current += char;
  }
  cells.push(current.trim());
  return cells;
}

function parseDelimited(text: string): string[][] | null {
  const normalized = String(text || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  if (!normalized) return null;
  const lines = normalized.split('\n').filter((line) => line.length > 0);
  if (!lines.length) return null;
  const hasTab = lines.some((line) => line.includes('\t'));
  const delimiter = hasTab ? '\t' : lines.some((line) => line.includes(';')) ? ';' : ',';
  const hasDelimitedRow = lines.some((line) => splitCsvLine(line, delimiter).length > 1);
  if (!hasTab && !hasDelimitedRow) return null;
  const grid = lines.map((line) => splitCsvLine(line, delimiter));
  if (grid.length === 1 && grid[0].length === 1) return null;
  return grid;
}

export function parseClipboardTable(htmlOrText: string): ClipboardTableParse | null {
  const raw = String(htmlOrText || '');
  if (!raw.trim()) return null;
  const fromHtml = /<table\b/i.test(raw) ? parseHtmlTable(raw) : null;
  const grid = fromHtml || parseDelimited(raw);
  if (!grid) return null;
  return truncateGrid(grid);
}
